import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { audienceSentence } from "../../../shared/goals/audience";
import { GoalConfigSchema } from "../../../shared/goals/config";
import { PlanSchema, type Plan } from "../../../shared/goals/plan";
import { useGoBack } from "../../../shared/phone/history";
import { useRail } from "../../../shared/phone/media";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { Chevron, PnRow } from "../../../shared/phone/Row";
import { ActionSheet } from "../../../shared/phone/Sheet";
import { Split } from "../../../shared/phone/Split";
import { Icon } from "../../components/ui/Icon";
import { Inline, Rich } from "../../components/ui/Rich";
import { ApiError } from "../../lib/api";
import { ccApi, type Version, type VersionFull } from "../../lib/ccApi";
import type { GoalView } from "../../lib/goalsApi";
import { MODE, PLAN_PARTS } from "../goal/FullPlan";
import { compareDocs } from "../library/parts";
import { ChangeList, whenOf, type Changes } from "../versions/Versions";
import { Pill, useScrolledPast } from "./GoalNative";
import { Mark } from "./Layout";
import { PART_FIELDS, planDoc } from "./planDoc";
import { andList, fmt, plural } from "./text";
import { useCcToast, useHome } from "./useCc";
import { useCan } from "../../context/AuthContext";

/**
 * A goal's plan on a phone and an iPad (the prototype's plan, its parts and its versions): "The plan" as a large
 * title with its version, the parts as rows with a line of each, one part filling the screen with Next at the
 * bottom, and every version with what changed. On an iPad the parts sit beside the open part.
 */

type PartId = (typeof PLAN_PARTS)[number][0];

/** Who made a version, in the owner's words (as the computer's plan page says it). */
function by(v: Version): string {
  if (v.made_in === "edit_goal") return "You, in Edit goal";
  if (v.made_by === "owner") return "You";
  if (v.made_by === "agent") return v.made_in === "setup" ? "The agent, approved by you" : "The agent";
  return v.made_by;
}
/** A goal that is approved: only then does an older version come back. */
const APPROVED = ["getting_ready", "live", "paused"];
/** JSON with every object's keys in order, so two versions read alike when only their key order differs. */
const canon = (v: unknown): unknown =>
  Array.isArray(v) ? v.map(canon) : v && typeof v === "object" ? Object.fromEntries(Object.keys(v).toSorted().map((k) => [k, canon((v as Record<string, unknown>)[k])])) : v;
const same = (a: unknown, b: unknown) => JSON.stringify(canon(a ?? null)) === JSON.stringify(canon(b ?? null));

/** The note to show once the versions list is back on screen, after a version came back from its own page. */
let flash: string | null = null;

const verLine = (state: string, v: number) => `Version ${v}, ${state === "live" ? "running" : state === "paused" ? "paused" : state === "done" ? "finished" : "approved"}`;

const USE = { retest: ["Re-test here", "violet"], fact: ["Used as fact", "good"], watch: ["Watch only", ""], ctx: ["Context", ""] } as const;
const useOf = (how: string): keyof typeof USE => {
  const h = how.toLowerCase();
  return /re-?test|guess|confirm/.test(h) ? "retest" : /fact/.test(h) ? "fact" : /observ|watch/.test(h) ? "watch" : "ctx";
};
const MODE_TONE = { ask: "violet", within_limits: "acc", on_its_own: "good" } as const;
const COLOURS = ["var(--k-acc)", "var(--k-violet)", "var(--k-warn)", "var(--k-dim)", "var(--k-good)", "var(--k-danger)"];
const hostOf = (u: string) => {
  const m = String(u).match(/https?:\/\/(?:www\.)?([^/\s]+)/g);
  return m ? [...new Set(m.map((x) => x.replace(/https?:\/\/(www\.)?/, "")))].slice(0, 2).join(" · ") : String(u).slice(0, 70);
};
const usd = (n: number) => `$${fmt(n)}`;

/** One line of a part, for its row in the list. */
function teaser(P: Plan, k: PartId): string {
  switch (k) {
    case "short":
      return P.summary.split(/(?<=[.!?])\s/)[0].replace(/\*\*/g, "");
    case "goal":
      return P.goal.pace_needed;
    case "found":
      return `${plural(P.findings.length, "finding")}, with where each came from`;
    case "lessons":
      return plural(P.lessons_used.length, "lesson");
    case "who":
      return !P.audience ? "Who the plan is for" : "places" in P.audience ? audienceSentence(P.audience).map((x) => x.text).join("") : P.audience.who;
    case "angles": {
      const first = P.angles.toSorted((a, b) => a.rank - b.rank)[0];
      return `${plural(P.angles.length, "idea")}${first ? `, “${first.name}” first` : ""}`;
    }
    case "tests":
      return `${plural(P.tests.length, "test")}${P.tests[0] ? `, decided on ${P.tests[0].metric}` : ""}`;
    case "money":
      return P.budget.split.map((b) => `${b.item} ${usd(b.usd)}`).join(", ");
    case "time":
      return `${plural(P.timeline.length, "step")}${P.timeline.length ? ` to ${P.timeline[P.timeline.length - 1].when}` : ""}`;
    case "run": {
      const own = P.autonomy.filter((a) => a.mode !== "ask").length;
      return `${plural(own, "thing")} on its own, ${P.autonomy.length - own} ask you first`;
    }
    case "risks":
      return plural(P.risks.length, "risk");
    case "asks":
      return plural(P.needs_from_you.length, "thing");
    default:
      return `Follows ${plural(P.playbook_followed.length, "part")}${P.conflicts.length ? `, pushes back on ${P.conflicts.length}` : ""}`;
  }
}

/** About how long the plan takes to read, at 220 words a minute. */
function minutes(P: Plan): number {
  const words = JSON.stringify(P)
    .replace(/"[a-z_]+":/g, " ")
    .split(/\s+/)
    .filter((w) => /[A-Za-z]/.test(w)).length;
  return Math.max(1, Math.round(words / 220));
}

function Kv({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <div className="pn-grp gp-kv">
      {rows.map(([k, v]) => (
        <div className="gp-kvr" key={k}>
          <span className="gp-kvk">{k}</span>
          <span className="gp-kvv">{v}</span>
        </div>
      ))}
    </div>
  );
}

/** One part of a plan, laid out the prototype's way from the plan's own words. */
function PartBody({ P, k }: { P: Plan; k: PartId }): ReactNode {
  switch (k) {
    case "short":
      return (
        <>
          <Rich block className="gp-rich gp-lead" text={P.summary} />
          <div className="gp-bet">
            <span>
              <Icon name="target" />
              What has to go right
            </span>
            <p>{P.bet}</p>
          </div>
          {P.choices.length > 0 && (
            <section>
              <div className="pn-cap">What it decided</div>
              <div className="pn-grp">
                {P.choices.map((c, i) => (
                  <div className="pn-row static wrap gp-tall" key={i}>
                    <span className="pn-ico">
                      <Icon name="check" />
                    </span>
                    <span className="m">
                      <span className="t gp-full">{c.choice.replace(/\.$/, "")}</span>
                      <span className="s">
                        <Inline text={c.why} />
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
          <div className="pn-card gp-can">
            <h3>Can it be done?</h3>
            <Rich block className="gp-rich" text={P.budget.reachable} />
          </div>
        </>
      );
    case "goal":
      return (
        <Kv
          rows={[
            ["Goal", P.goal.statement],
            ["One result", P.goal.result],
            ["Pace", P.goal.pace_needed],
            ...(P.goal.assumptions.length
              ? ([
                  [
                    "Assumptions",
                    <ul className="gp-ul" key="a">
                      {P.goal.assumptions.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>,
                  ],
                ] as [string, ReactNode][])
              : []),
          ]}
        />
      );
    case "found":
      return (
        <div className="pn-grp">
          {P.findings.map((x, i) => (
            <div className="pn-row static wrap gp-tall" key={i}>
              <span className="gp-n">{String(i + 1).padStart(2, "0")}</span>
              <span className="m">
                <span className="t gp-full gp-fw">{x.finding}</span>
                <span className="gp-src">
                  <Icon name="globe" />
                  {hostOf(x.source)}
                </span>
              </span>
            </div>
          ))}
        </div>
      );
    case "lessons":
      return (
        <div className="pn-grp">
          {P.lessons_used.map((u, i) => {
            const [word, tone] = USE[useOf(u.how)];
            return (
              <div className="pn-row static wrap gp-tall" key={i}>
                <span className="gp-lid">{u.id}</span>
                <span className="m">
                  <span className="t gp-full gp-fw">{u.how}</span>
                </span>
                <Pill tone={tone}>{word}</Pill>
              </div>
            );
          })}
        </div>
      );
    case "who": {
      const a = P.audience;
      if (!a) return <p className="pn-note">This plan has no Meta audience.</p>;
      if ("places" in a) {
        const TITLES = { places: "Why these places", ages: "Why these ages", starts: "Why these starting points", lists: "Why these lists", reach: "How far Meta may look" } as const;
        return (
          <Kv
            rows={[
              ["Who", audienceSentence(a).map((x) => x.text).join("")],
              ["How many", a.size_basis],
              ...Object.entries(a.why).map(([part, why]) => [TITLES[part as keyof typeof TITLES] ?? part, why] as [string, ReactNode]),
            ]}
          />
        );
      }
      return (
        <Kv
          rows={[
            ["Who", a.who],
            ["How many", `${a.size}. ${a.size_basis}`],
            ["Where", a.where],
          ]}
        />
      );
    }
    case "angles":
      return (
        <div className="gp-cards">
          {P.angles
            .toSorted((x, y) => x.rank - y.rank)
            .map((a, i) => (
              <article className={`pn-card gp-ang${i === 0 ? " first" : ""}`} key={i}>
                <span className="gp-rank">{i === 0 ? "Goes first" : `Idea ${i + 1}`}</span>
                <h3>{a.name}</h3>
                <p className="gp-msg">{a.message}</p>
                <blockquote className="gp-hook">“{a.hook}”</blockquote>
                <dl className="gp-dl">
                  <div>
                    <dt>Why</dt>
                    <dd>{a.why}</dd>
                  </div>
                  {a.evidence.length > 0 && (
                    <div>
                      <dt>Based on</dt>
                      <dd>
                        <ul className="gp-ul">
                          {a.evidence.map((e, j) => (
                            <li key={j}>{e}</li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt>Risk</dt>
                    <dd>{a.risk}</dd>
                  </div>
                </dl>
              </article>
            ))}
        </div>
      );
    case "tests":
      return (
        <div className="gp-cards">
          {P.tests.map((t, i) => (
            <article className="pn-card gp-test" key={i}>
              <span className="gp-lid">{t.id}</span>
              <h3>{t.name}</h3>
              <p className="gp-muted">{t.hypothesis}</p>
              <div className="gp-arms">
                {t.variants.map((v, j) => (
                  <div key={j}>
                    <b>{String.fromCharCode(65 + j)}</b>
                    <span>{v.replace(/^[A-D][:.)]\s*/, "")}</span>
                  </div>
                ))}
              </div>
              <div className="gp-chips">
                <span>Decides on {t.metric}</span>
                <span>
                  {t.budget_usd ? `${usd(t.budget_usd)} · ` : ""}
                  {plural(t.days, "day")}
                </span>
              </div>
              <div className="gp-need">
                <b>Needs</b> <Inline text={t.min_data} />
              </div>
              <p className="gp-stop">
                <Icon name="shield" />
                <span>Stops early if {t.stop_early_if.replace(/\.$/, "").replace(/^./, (c) => c.toLowerCase())}</span>
              </p>
            </article>
          ))}
        </div>
      );
    case "money": {
      const tot = P.budget.split.reduce((a, b) => a + b.usd, 0);
      return (
        <>
          <div className="pn-card gp-money">
            <div className="gp-pt">
              <b className="gp-big">{usd(tot)}</b>
              <span>in total</span>
            </div>
            <div className="gp-split">
              {P.budget.split.map((b, i) => (
                <i key={i} style={{ width: `${tot ? (b.usd / tot) * 100 : 0}%`, background: COLOURS[i % COLOURS.length] }} />
              ))}
            </div>
            <div className="gp-leg">
              {P.budget.split.map((b, i) => (
                <div key={i}>
                  <span className="gp-dot" style={{ background: COLOURS[i % COLOURS.length] }} />
                  <span>{b.item}</span>
                  <b>{usd(b.usd)}</b>
                </div>
              ))}
            </div>
          </div>
          <div className="pn-grp">
            <div className="pn-row static wrap gp-tall">
              <span className="pn-ico">
                <Icon name="dollar" />
              </span>
              <span className="m">
                <span className="t gp-full gp-fw">
                  <Inline text={P.budget.cost_per_result} />
                </span>
              </span>
            </div>
            <div className="pn-row static wrap gp-tall">
              <span className="pn-ico good">
                <Icon name="chart" />
              </span>
              <span className="m">
                <span className="t">What it expects</span>
                <span className="s">{P.budget.expected_results}</span>
              </span>
            </div>
          </div>
        </>
      );
    }
    case "time":
      return (
        <ol className="gp-tl">
          {P.timeline.map((r, i) => (
            <li key={i}>
              <span className="gp-tw">{r.when}</span>
              <span className="gp-tx">{r.what}</span>
            </li>
          ))}
        </ol>
      );
    case "run":
      return (
        <section>
          <div className="pn-cap">What it may do</div>
          <div className="pn-grp">
            {P.autonomy.map((a, i) => (
              <div className="pn-row static wrap gp-tall" key={i}>
                <span className="m">
                  <span className="t gp-full">{a.action}</span>
                  <span className="s">{a.why}</span>
                </span>
                <Pill tone={MODE_TONE[a.mode]}>{MODE[a.mode]}</Pill>
              </div>
            ))}
          </div>
        </section>
      );
    case "risks":
      return (
        <>
          <div className="gp-cards">
            {P.risks.map((r, i) => (
              <article className="pn-card gp-risk" key={i}>
                <h3>{r.risk}</h3>
                <dl className="gp-dl">
                  <div>
                    <dt>Warning sign</dt>
                    <dd>{r.signal}</dd>
                  </div>
                  <div>
                    <dt>What it will do</dt>
                    <dd>{r.response}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
          {P.change_course_if.length > 0 && (
            <section>
              <div className="pn-cap">It will change course if</div>
              <div className="pn-grp">
                {P.change_course_if.map((t, i) => (
                  <div className="pn-row static wrap" key={i}>
                    <span className="pn-ico warn">
                      <Icon name="bolt" />
                    </span>
                    <span className="m">
                      <span className="t gp-full gp-fw">{t}</span>
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      );
    case "asks":
      return (
        <div className="pn-grp">
          {P.needs_from_you.map((t, i) => (
            <div className="pn-row static wrap" key={i}>
              <span className="gp-ck" />
              <span className="m">
                <span className="t gp-full">{t}</span>
              </span>
            </div>
          ))}
        </div>
      );
    default:
      return (
        <>
          <div className="pn-grp">
            {P.playbook_followed.map((x, i) => (
              <div className="pn-row static wrap gp-tall" key={i}>
                <span className="pn-ico good">
                  <Icon name="check" />
                </span>
                <span className="m">
                  <span className="t gp-full">{x.rule}</span>
                  <span className="s">{x.how}</span>
                </span>
              </div>
            ))}
          </div>
          {P.conflicts.length > 0 && (
            <section>
              <div className="pn-cap gp-warn-c">Where it pushes back</div>
              <div className="gp-cards">
                {P.conflicts.map((x, i) => (
                  <article className="pn-card gp-push" key={i}>
                    <h3>{x.rule}</h3>
                    <dl className="gp-dl">
                      <div>
                        <dt>Its evidence</dt>
                        <dd>{x.evidence}</dd>
                      </div>
                      <div>
                        <dt>It suggests</dt>
                        <dd>{x.suggestion}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      );
  }
}

// --- The plan's data -----------------------------------------------------------------------------------

/** The plan in force, its versions, and the version before it for "Show what changed". */
function usePlanData(view: GoalView, wantPrev: boolean) {
  const g = view.goal;
  const cur = view.current;
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [metaHolds, setMetaHolds] = useState(false);
  const [prev, setPrev] = useState<VersionFull | null | undefined>(undefined);
  const before = (cur?.version ?? 0) - 1;
  useEffect(() => {
    ccApi.versions(g.id).then(
      (r) => {
        setVersions(r.versions);
        setMetaHolds(r.metaHolds);
      },
      () => setVersions([]),
    );
  }, [g.id, cur?.version]);
  useEffect(() => {
    setPrev(undefined);
  }, [cur?.version]);
  useEffect(() => {
    if (!wantPrev || prev !== undefined || before < 1) return;
    ccApi.version(g.id, before).then((r) => setPrev(r.version), () => setPrev(null));
  }, [wantPrev, prev, before, g.id]);
  const plan = cur?.plan ?? null;
  const old = prev?.plan ?? null;
  const changed = (id: string) => !!(old && plan && (PART_FIELDS[id] ?? []).some((k) => !same(old[k], plan[k])));
  // Why: a plan the owner never approved never ran; only versions that were in force can come back.
  const shown = (versions ?? []).filter((v) => v.in_force || v.version === cur?.version);
  return { plan, old, before, prev, changed, versions, shown, metaHolds };
}

function GoalRef({ view }: { view: GoalView }) {
  const { home } = useHome();
  const product = home?.products.find((p) => p.goals.some((x) => x.id === view.goal.id)) ?? null;
  return (
    <p className="gp-gref">
      {product && <Mark p={product} />}
      <span>{view.goal.sentence}</span>
    </p>
  );
}

function ShowChanged({ on, before, onChange }: { on: boolean; before: number; onChange: (on: boolean) => void }) {
  return (
    <div className="pn-grp">
      <button type="button" className="pn-row" role="switch" aria-checked={on} onClick={() => onChange(!on)}>
        <span className="m">
          <span className="t">Show what changed</span>
          <span className="s">Since version {before}</span>
        </span>
        <span className="pn-sw" aria-checked={on} aria-hidden="true" />
      </button>
    </div>
  );
}

/** The parts as rows: a number, the part's name and a line of it, "Changed" on the ones that moved. */
function PartRows({ P, base, show, changed, on, replace = false }: { P: Plan; base: string; show: boolean; changed: (id: string) => boolean; on?: string; replace?: boolean }) {
  return (
    <div className="pn-grp">
      {PLAN_PARTS.map(([id, title], i) => (
        <Link key={id} className={`pn-row gp-pr${on === id ? " on" : ""}`} to={`${base}/${id}${show ? "?changed=1" : ""}`} replace={replace} aria-current={on === id ? "page" : undefined}>
          <span className="gp-n">{i + 1}</span>
          <span className="m">
            <span className="t">{title}</span>
            <span className="s">{teaser(P, id)}</span>
          </span>
          {show && changed(id) && <span className="gp-chg">Changed</span>}
          <Chevron />
        </Link>
      ))}
    </div>
  );
}

/** One part's own page: its place in the plan, its title, what it says, and how it read before when it changed. */
function PartPage({ P, old, at, show, changed, before }: { P: Plan; old: Plan | null; at: number; show: boolean; changed: boolean; before: number }) {
  const [id, title] = PLAN_PARTS[at];
  return (
    <>
      <div className="gp-ph1">
        <span className="gp-kick">
          Part {at + 1} of {PLAN_PARTS.length}
        </span>
        <h1>{title}</h1>
        {show && changed && <span className="gp-chg">Changed in this version</span>}
      </div>
      <div className="pn-stack tight gp-pb">
        <PartBody P={P} k={id} />
        {show && changed && old && (
          <details className="gp-old">
            <summary>
              <Icon name="chev" />
              How it read in version {before}
            </summary>
            <div className="pn-stack tight">
              <PartBody P={old} k={id} />
            </div>
          </details>
        )}
      </div>
    </>
  );
}

// --- The plan's screens --------------------------------------------------------------------------------

/** `/goals/:id/plan`, `/plan/:part`, `/plan/versions` and `/plan/versions/:n` on a phone and an iPad. */
export function PlanNative({ view, reload, sub, more }: { view: GoalView; reload: () => Promise<void>; sub: string | undefined; more: string | undefined }) {
  if (sub === "versions") return more ? <VersionNative view={view} reload={reload} n={Number(more)} /> : <VersionsNative view={view} />;
  return <PlanScreen view={view} part={sub} />;
}

function PlanScreen({ view, part }: { view: GoalView; part: string | undefined }) {
  const g = view.goal;
  const goalPath = `/goals/${encodeURIComponent(g.id)}`;
  const base = `${goalPath}/plan`;
  const rail = useRail();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [params, setParams] = useSearchParams();
  const show = params.get("changed") === "1";
  const { plan, old, before, changed, shown } = usePlanData(view, show);
  const at = PLAN_PARTS.findIndex(([id]) => id === part);
  const screen = useRef<HTMLDivElement>(null);
  const scrolled = useScrolledPast(screen, 64);
  const version = view.current?.version ?? g.version;
  const setShow = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set("changed", "1");
    else next.delete("changed");
    setParams(next, { replace: true });
  };
  const moved = PLAN_PARTS.filter(([id]) => changed(id)).map(([, t]) => t);
  const q = show ? "?changed=1" : "";

  if (!plan)
    return (
      <div className="pn-screen gp-plan">
        <PhoneNav title="The plan" large back={{ to: goalPath, label: "Goal" }} />
        <div className="pn-empty">
          <b>No plan yet</b>The agent writes the plan when the goal is set.
        </div>
      </div>
    );

  const list = (
    <div className="pn-stack tight">
      <GoalRef view={view} />
      {before >= 1 && (
        <section>
          <ShowChanged on={show} before={before} onChange={setShow} />
          {show && <p className="pn-note">{moved.length ? `What changed in the plan: ${andList(moved)}.` : old ? "The plan's parts read the same." : "Reading the version before…"}</p>}
        </section>
      )}
      <section>
        <div className="pn-cap">
          {PLAN_PARTS.length} parts, about {plural(minutes(plan), "minute")}
        </div>
        <PartRows P={plan} base={base} show={show} changed={changed} on={rail ? PLAN_PARTS[Math.max(0, at)][0] : undefined} replace={rail} />
      </section>
      <section>
        <div className="pn-grp">
          <PnRow to={`${base}/versions`} icon={<Icon name="branch" />} iconTone="plain" title="Versions" value={String(shown.length || version)} />
        </div>
      </section>
    </div>
  );

  // An iPad: the parts beside the open part, the first part open until another is picked.
  if (rail) {
    const i = Math.max(0, at);
    const next = PLAN_PARTS[i + 1];
    return (
      <Split
        listWidth={320}
        showDetail
        list={
          <div className="pn-screen gp-plan">
            <PhoneNav title="The plan" large sub={verLine(g.state, version)} back={{ to: goalPath, label: "Goal" }} />
            {list}
          </div>
        }
        detail={
          <div className="pn-screen gp-part gp-part-ip">
            <PartPage P={plan} old={old} at={i} show={show} changed={changed(PLAN_PARTS[i][0])} before={before} />
            {next && (
              <div className="pn-foot">
                <button type="button" className="pn-btn pri wide" onClick={() => navigate(`${base}/${next[0]}${q}`, { replace: true })}>
                  Next: {next[1]}
                  <Icon name="right" />
                </button>
              </div>
            )}
          </div>
        }
      />
    );
  }

  // A phone: one part fills the screen, with Next in its bottom bar.
  if (at >= 0) {
    const next = PLAN_PARTS[at + 1];
    return (
      <div className={`pn-screen gp-part${scrolled ? " gp-scrolled" : ""}`} ref={screen} key={part}>
        <PhoneNav title={<span>{PLAN_PARTS[at][1]}</span>} back={{ to: `${base}${q}`, label: "The plan" }} />
        <PartPage P={plan} old={old} at={at} show={show} changed={changed(PLAN_PARTS[at][0])} before={before} />
        <div className="pn-foot">
          {next ? (
            <button type="button" className="pn-btn pri wide" onClick={() => navigate(`${base}/${next[0]}${q}`, { replace: true })}>
              Next: {next[1]}
              <Icon name="right" />
            </button>
          ) : (
            <button type="button" className="pn-btn wide" onClick={() => goBack(`${base}${q}`)}>
              Back to the plan
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pn-screen gp-plan">
      <PhoneNav title="The plan" large sub={verLine(g.state, version)} back={{ to: goalPath, label: "Goal" }} />
      {list}
    </div>
  );
}

/** Who made each version, when and why, newest first; each opens what changed. */
function VersionsNative({ view }: { view: GoalView }) {
  const g = view.goal;
  const base = `/goals/${encodeURIComponent(g.id)}/plan`;
  const { versions, shown } = usePlanData(view, false);
  const cur = view.current?.version ?? g.version;
  const { toast, say } = useCcToast();
  useEffect(() => {
    if (!flash) return;
    say(flash);
    flash = null;
  }, [say]);
  return (
    <div className="pn-screen gp-vers">
      <PhoneNav title="Versions" large sub={`Version ${cur} is in force`} back={{ to: base, label: "The plan" }} />
      <div className="pn-stack">
        {versions === null ? (
          <p className="pn-note">Loading the versions…</p>
        ) : (
          <section>
            <div className="pn-grp">
              {shown.map((v) => (
                <Link className="pn-row wrap gp-tall" key={v.id} to={`${base}/versions/${v.version}`}>
                  <span className={`gp-n${v.version === cur ? " on" : ""}`}>{v.version}</span>
                  <span className="m">
                    <span className="t gp-tp">
                      Version {v.version}
                      {v.version === cur && <Pill tone="good">In force</Pill>}
                    </span>
                    {v.summary && <span className="s">{v.summary}</span>}
                    <span className="s">
                      {by(v)} · {whenOf(v.created_at)}
                    </span>
                  </span>
                  <Chevron />
                </Link>
              ))}
            </div>
            <p className="pn-note">Every change is a new version. Nothing is deleted.</p>
          </section>
        )}
      </div>
      {toast}
    </div>
  );
}

/** One version: what changed from the one before it (or, for an older one, to the one in force), line by line, and Use this version. */
function VersionNative({ view, reload, n }: { view: GoalView; reload: () => Promise<void>; n: number }) {
  const g = view.goal;
  const base = `/goals/${encodeURIComponent(g.id)}/plan`;
  const goBack = useGoBack();
  const { toast, say } = useCcToast();
  const { versions, shown, metaHolds } = usePlanData(view, false);
  const cur = view.current?.version ?? g.version;
  const v = (versions ?? []).find((x) => x.version === n) ?? null;
  const isCur = n === cur;
  const older = shown.filter((x) => x.version < n).toSorted((a, b) => b.version - a.version)[0] ?? null;
  const [from, to] = isCur ? [older?.version ?? null, n] : [n, cur];
  const [res, setRes] = useState<Changes | null | undefined>(undefined);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (a: number, b: number): Promise<Changes> => {
    const [x, y] = await Promise.all([ccApi.version(g.id, a), ccApi.version(g.id, b)]);
    const doc = (w: VersionFull) => planDoc(GoalConfigSchema.parse(w.config), w.plan ? PlanSchema.parse(w.plan) : null);
    return compareDocs(doc(x.version), doc(y.version));
  }, [g.id]);
  useEffect(() => {
    if (from === null || versions === null) return;
    let alive = true;
    setRes(undefined);
    load(from, to).then(
      (r) => alive && setRes(r),
      () => alive && setRes(null),
    );
    return () => {
      alive = false;
    };
  }, [from, to, load, versions]);
  const next = Math.max(0, ...(versions ?? []).map((x) => x.version)) + 1;
  const can = useCan();
  const canUse = !isCur && APPROVED.includes(g.state) && !!v && (v.in_force || v.version === cur) && can("editor");
  const parts = useMemo(() => (res ? res.parts.filter((p) => PLAN_PARTS.some(([id]) => id === p.id)) : []), [res]);

  const bringBack = async () => {
    if (!view.current) return;
    setBusy(true);
    try {
      const r = await ccApi.useVersion(g.id, n, view.current.version);
      await reload();
      flash = `Version ${n} is back, as version ${r.version}.`;
      goBack(`${base}/versions`);
    } catch (e) {
      say(e instanceof ApiError ? (/Nothing changed/.test(e.message) ? "That is the version in force already." : e.message) : "That did not work. Try again.", { done: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pn-screen gp-ver">
      <PhoneNav title={`Version ${n}`} large sub={v ? `${by(v)}, ${whenOf(v.created_at)}` : undefined} back={{ to: `${base}/versions`, label: "Versions" }} />
      <div className="pn-stack">
        {v?.summary && <p className="gp-lead pn-pad">{v.summary}</p>}
        {from === null ? (
          <p className="pn-note">This is the first version, so there is nothing to compare yet.</p>
        ) : res === undefined ? (
          <p className="pn-note">Reading both versions…</p>
        ) : res === null ? (
          <p className="pn-note">Those versions did not load. Go back and try again.</p>
        ) : (
          <>
            <section>
              <div className="pn-sh">
                <h2>
                  From version {from} to {to}
                </h2>
              </div>
              <ChangeList res={res} same={false} />
              {parts.length > 0 && <p className="pn-note">What changed in the plan: {andList(parts.map((p) => p.title))}.</p>}
            </section>
            {parts.length > 0 && (
              <section>
                <div className="pn-cap">The parts that changed</div>
                <div className="pn-grp">
                  {parts.map((p) => (
                    <PnRow key={p.id} to={`${base}/${p.id}?changed=1`} title={p.title} sub={view.current?.plan ? teaser(view.current.plan, p.id as PartId) : undefined} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
      {canUse && (
        <div className="pn-foot">
          <button type="button" className="pn-btn pri wide" disabled={busy} onClick={() => setAsking(true)}>
            Use this version
          </button>
        </div>
      )}
      <ActionSheet
        open={asking}
        onClose={() => setAsking(false)}
        title={`Bring back version ${n}?`}
        message={`It becomes version ${next}, in force from now. Nothing is deleted.${metaHolds ? " Where the ads show and who sees them stay as they are on Meta." : ""}`}
        actions={[{ label: "Approve", onSelect: () => void bringBack() }]}
      />
      {toast}
    </div>
  );
}

